import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getBaseUrl } from './ApiContext';
import LoadingIndicator from './LoadingIndicator';
import './LPTool.css';

const baseUrl = getBaseUrl();

// The control for the LP outreach extension, deliberately OUTSIDE the
// extension. A switch that lives inside the tool is unreachable when the
// tool is what broke, and invisible to someone who has not installed it
// yet. This page is also the only download link: the bucket is private, so
// there is no URL to send around.
//
// The setting is per person and applies to every browser they sign into
// Kyber Drive from. It does not touch anyone else's Kyber, and it moves no
// deployment: each version is a Cloud Run revision that is already running
// and already serving none of the normal traffic.
function LPTool() {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState('');

    const load = useCallback(() => {
        axios.get(`${baseUrl}/api/lp-tool/version`)
            .then((r) => setState(r.data))
            .catch(() => setError('Could not read your current setting.'));
    }, []);

    useEffect(load, [load]);

    const choose = (id) => {
        if (busy || state?.version === id) return;
        setBusy(true);
        setError('');
        axios.post(`${baseUrl}/api/lp-tool/version`, { version: id })
            .then((r) => setState(r.data))
            .catch(() => setError('That did not save. Try again.'))
            .finally(() => setBusy(false));
    };

    // The zip comes back through the API rather than from a link, because
    // the bucket is private and uniform-access: this route is the link.
    const download = (build) => {
        setDownloading(build || 'latest');
        axios.get(`${baseUrl}/api/lp-tool/download`,
                  { responseType: 'blob', params: build ? { build } : {} })
            .then((r) => {
                const url = URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = build ? `Kyber extension ${build}.zip` : 'Kyber extension.zip';
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            })
            .catch(() => setError('The download failed. Tell Afnaan.'))
            .finally(() => setDownloading(''));
    };

    if (!state) {
        return (
            <div className="lptool">
                <h1>Extension details</h1>
                {error ? <p className="lptool-error">{error}</p> : <LoadingIndicator />}
            </div>
        );
    }

    return (
        <div className="lptool">
            <h1>Extension details</h1>
            <p className="lptool-lead">
                Two separate things live on this page. <b>The version</b> is
                what your extension talks to, and you change it here.{' '}
                <b>The extension</b> is the thing installed in your Chrome,
                and it only changes when you download a new copy. Changing
                one never changes the other.
            </p>

            {state.pinned && (
                <div className="lptool-pinned">
                    <b>You are on an older version</b>
                    <span>
                        Kyber is set to “{state.current.label}”:{' '}
                        {state.current.note}. Newer improvements are off
                        until you switch back.
                    </span>
                    <button
                        className="lptool-primary"
                        onClick={() => choose('current')}
                        disabled={busy}
                    >
                        Switch back to current
                    </button>
                </div>
            )}

            <h2>If something looks wrong</h2>
            <p className="lptool-hint">
                Put yourself back on an older version. It takes effect in
                your browser within a minute, on its own: nothing to
                download, nothing to reinstall, and nothing changes for
                anyone else on the team.
            </p>
            <div className="lptool-options">
                {state.options.map((opt) => (
                    <button
                        key={opt.id}
                        className="lptool-opt"
                        role="radio"
                        aria-checked={opt.id === state.version}
                        disabled={busy}
                        onClick={() => choose(opt.id)}
                    >
                        <b>{opt.label}</b>
                        <span>{opt.note}</span>
                    </button>
                ))}
            </div>
            {error && <p className="lptool-error">{error}</p>}

            <h2>The Chrome extension</h2>
            <p className="lptool-hint">
                This is the tool itself, not the setting above. You only
                need it when a new build is announced. Downloading leaves
                your version choice exactly as it is.
            </p>
            <div className="lptool-build">
                <div className="lptool-build-what">
                    <b>
                        {state.latest_build
                            ? `Latest build: ${state.latest_build.version}`
                            : 'Latest build'}
                    </b>
                    <span>
                        {state.latest_build?.uploaded
                            ? `published ${new Date(state.latest_build.uploaded)
                                .toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}. `
                            : ''}
                        The Kyber icon tells you which build you have, and
                        turns green when a newer one is waiting.
                    </span>
                </div>
                <button className="lptool-primary" onClick={() => download()}
                        disabled={!!downloading}>
                    {downloading === 'latest' ? 'Preparing…' : 'Download'}
                </button>
            </div>
            <ol className="lptool-steps">
                <li>
                    <b>Delete your old Kyber folder first.</b> Unzipping does
                    not replace it: macOS makes a second folder called
                    &ldquo;Kyber extension 2&rdquo; and Chrome carries on
                    loading the old one, so nothing appears to change.
                </li>
                <li>Unzip the download.</li>
                <li>
                    In <code>chrome://extensions</code>, click the reload
                    arrow on the Kyber card. First time only: turn on{' '}
                    <b>Developer mode</b>, then <b>Load unpacked</b> and pick
                    the unzipped folder.
                </li>
                <li>
                    <b>Pin the Kyber icon</b> to your toolbar: click the
                    puzzle piece to the right of the address bar, then the pin
                    beside Kyber. Unpinned, the icon is hidden in that menu
                    and you will never see it turn amber or green.
                </li>
            </ol>

            {state.builds?.length > 1 && (
                <>
                    <h2>Earlier builds</h2>
                    <p className="lptool-hint">
                        If a new build is the problem, the previous one is
                        here. Same two steps to put it back.
                    </p>
                    <div className="lptool-options">
                        {state.builds.slice(1).map((b) => (
                            <div className="lptool-build" key={b.version}>
                                <div className="lptool-build-what">
                                    <b>Build {b.version}</b>
                                    <span>
                                        {b.published
                                            ? `published ${new Date(b.published)
                                                .toLocaleDateString(undefined,
                                                    { day: 'numeric', month: 'short' })}`
                                            : 'published earlier'}
                                    </span>
                                </div>
                                <button className="lptool-primary"
                                        onClick={() => download(b.version)}
                                        disabled={!!downloading}>
                                    {downloading === b.version ? 'Preparing…' : 'Download'}
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <p className="lptool-foot">
                The Kyber icon pauses the tool and shows which version and
                which build you are on. Switching versions happens here, so
                that it still works on a day when the extension itself is
                the thing misbehaving.
            </p>
        </div>
    );
}

export default LPTool;
